<template>
    <OnboardingLayout v-bind="appSharedData.getOnboardingLayoutProps()">
        <template #content>
            <OnboardingStepContainer
                :showStepsIndicator="true"
                :stepNumber="6"
            >
                <template #title>Tell us a bit about your business</template>
                <template #subtitle>
                    This helps us personalize TinyCal and improve the product
                    over time.
                </template>
                <template #content>
                    <div class="max-w-xl mx-auto w-full">
                        <ApiForm
                            method="POST"
                            :url="
                                RoutePathAppEnum.Onboarding_Step6_BusinessInfo
                            "
                            ref="apiFormRef"
                        >
                            <div class="space-y-4">
                                <Card>
                                    <template #title>
                                        <CardTitle
                                            title="Is it just you or a team?"
                                        />
                                    </template>
                                    <template #content>
                                        <FormControl
                                            hideLabel
                                            :error="
                                                apiFormRef?.response?.getValidationError(
                                                    RequestBodyKeyEnum.App_Onboarding_NumberOfEmployees,
                                                )
                                            "
                                        >
                                            <div class="space-y-2">
                                                <template
                                                    v-for="(
                                                        numberOfEmployees, index
                                                    ) in numberOfEmployeesOptions"
                                                    :key="index"
                                                >
                                                    <RadioInput
                                                        :name="
                                                            RequestBodyKeyEnum.App_Onboarding_NumberOfEmployees
                                                        "
                                                        :label="
                                                            numberOfEmployees
                                                        "
                                                        :checked="false"
                                                        :value="
                                                            numberOfEmployees
                                                        "
                                                        v-model="
                                                            dummySelectedNumberOfEmployees
                                                        "
                                                    />
                                                </template>
                                            </div>
                                        </FormControl>
                                    </template>
                                </Card>
                                <Card>
                                    <template #title>
                                        <CardTitle
                                            title="Which option best describes your business?"
                                        />
                                    </template>
                                    <template #content>
                                        <FormControl
                                            hideLabel
                                            :error="
                                                apiFormRef?.response?.getValidationError(
                                                    RequestBodyKeyEnum.App_Onboarding_Industry,
                                                )
                                            "
                                        >
                                            <SelectInput
                                                :name="
                                                    RequestBodyKeyEnum.App_Onboarding_Industry
                                                "
                                                v-model="dummySelectedIndustry"
                                            >
                                                <template
                                                    v-for="(
                                                        industry, index
                                                    ) in industryOptions"
                                                    :key="index"
                                                >
                                                    <option :value="industry">
                                                        {{ industry }}
                                                    </option>
                                                </template>
                                            </SelectInput>
                                            <div
                                                v-if="
                                                    dummySelectedIndustry ===
                                                    'Other'
                                                "
                                            >
                                                <FormControl
                                                    required
                                                    label="Tell us which"
                                                    :error="
                                                        apiFormRef?.response?.getValidationError(
                                                            RequestBodyKeyEnum.App_Onboarding_Industry,
                                                        )
                                                    "
                                                >
                                                    <TextInput
                                                        :name="
                                                            RequestBodyKeyEnum.App_Onboarding_Industry
                                                        "
                                                        required
                                                    />
                                                </FormControl>
                                            </div>
                                        </FormControl>
                                    </template>
                                </Card>
                                <Card>
                                    <template #content>
                                        <div>
                                            <FormControl
                                                label="Are you switching from another scheduling tool?"
                                                :error="
                                                    apiFormRef?.response?.getValidationError(
                                                        RequestBodyKeyEnum.App_Onboarding_PreviousSchedulingTool,
                                                    )
                                                "
                                            >
                                                <SelectInput
                                                    :name="
                                                        RequestBodyKeyEnum.App_Onboarding_PreviousSchedulingTool
                                                    "
                                                >
                                                    <option value="">
                                                        None
                                                    </option>
                                                    <option value="Calendly">
                                                        Calendly
                                                    </option>
                                                    <option value="SavvyCal">
                                                        SavvyCal
                                                    </option>
                                                    <option value="Koalendar">
                                                        Koalendar
                                                    </option>
                                                    <option value="ZenCal">
                                                        ZenCal
                                                    </option>
                                                    <option value="TidyCal">
                                                        TidyCal
                                                    </option>
                                                </SelectInput>
                                            </FormControl>
                                            <FormControl
                                                label="Website URL"
                                                :error="
                                                    apiFormRef?.response?.getValidationError(
                                                        RequestBodyKeyEnum.App_Onboarding_WebsiteUrl,
                                                    )
                                                "
                                            >
                                                <TextInput
                                                    prefix="https://"
                                                    :name="
                                                        RequestBodyKeyEnum.App_Onboarding_WebsiteUrl
                                                    "
                                                    v-model="dummyWebsiteUrl"
                                                />
                                            </FormControl>
                                        </div>
                                    </template>
                                </Card>
                            </div>
                        </ApiForm>
                    </div>
                </template>
            </OnboardingStepContainer>
        </template>

        <template #footerRight>
            <Button
                @click="apiFormRef?.submit()"
                iconName="arrow-right"
                color="primary"
                :iconRight="true"
                :loading="apiFormRef?.loading.valueOf()"
            >
                Continue
            </Button>
        </template>
    </OnboardingLayout>
</template>

<script setup lang="ts">
    import { ref } from 'vue'
    import OnboardingLayout from '@/Shared/Layout/Onboarding/OnboardingLayout.vue'
    import Button from '@/Shared/Component/Button.vue'
    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import OnboardingStepContainer from '@/App/Company/Component/OnboardingStepContainer.vue'
    import type { ApiFormRefType } from '@/Shared/AutoGenerated/Types'
    import Card from '@/Shared/Component/Card.vue'
    import CardTitle from '@/Shared/Component/CardTitle.vue'
    import RadioInput from '@/Shared/Component/Forms/RadioInput.vue'
    import TextInput from '@/Shared/Component/Forms/TextInput.vue'
    import SelectInput from '@/Shared/Component/Forms/SelectInput.vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import AppSharedData from '@/Shared/Entity/AppSharedData'
    import Onboarding from '@/App/Company/Entity/Onboarding'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import { RoutePathAppEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
    import type { OnboardingRawData } from '@/Shared/AutoGenerated/Main'

    interface ShowOnboardingStepBusinessInfoProps {
        appSharedDataData: any
        onboardingRawData: OnboardingRawData
        industryOptions: string[]
        numberOfEmployeesOptions: string[]
    }

    const props = defineProps<ShowOnboardingStepBusinessInfoProps>()
    const appSharedData = AppSharedData.hydrate(props.appSharedDataData)
    const onboarding = Onboarding.hydrateSingle(props.onboardingRawData)
    const apiFormRef = ref<ApiFormRefType>(null)

    const dummySelectedIndustry = ref<string | undefined>(undefined)
    const dummySelectedNumberOfEmployees = ref<string | undefined>(undefined)
    const dummyWebsiteUrl = ref<string | null>(onboarding.surveyWebsiteUrl)
</script>
